import { AgmInfoWindow } from '@agm/core';
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { BuildingModel } from 'src/app/shared/models/building.model';
import {
  BuildingManagementType,
  LanguageType,
  MapModeType,
} from 'src/app/shared/models/enum.model';
import { AccountInfo } from './../../shared/models/accountInfo.model';
import { BuildingFilterModel } from './../../shared/models/building-filter.model';
import { DashboardModel } from './../../shared/models/dashboard.model';
import { MapClusterModel } from './../../shared/models/map-cluster.model';
import { MunicipalityModel } from './../../shared/models/municipality.model';
import { PeriodModel } from './../../shared/models/period.model';
import { SearchStreet } from './../../shared/models/search-street.model';
import {
  ClearSelectedBuilding,
  FilterBuildings,
  GetAllBuildingMarkers,
  SearchByStreet,
} from './../../shared/ngxs/action/building.action';
import { GetDashboard } from './../../shared/ngxs/action/dashboard.action';
import { FilterMunicipalitiesWithoutFiltering } from './../../shared/ngxs/action/municipality.action';
import { FilterPeriods } from './../../shared/ngxs/action/period.action';
import { AccountState } from './../../shared/ngxs/state/account.state';
import { BuildingState } from './../../shared/ngxs/state/building.state';
import { DashboardState } from './../../shared/ngxs/state/dashboard.state';
import { MunicipalityState } from './../../shared/ngxs/state/municipality.state';
import { PeriodState } from './../../shared/ngxs/state/period.state';
import { FilterService } from './../../shared/services/filter.service';
import { LocalizationService } from './../../shared/services/localization.service';

@Component({
  selector: 'app-buildings-map',
  templateUrl: './buildings-map.component.html',
  styleUrls: ['./buildings-map.component.scss'],
  providers: [FilterService],
})
export class BuildingsMapComponent implements OnInit, OnDestroy {
  // google maps zoom level

  @ViewChildren('infoWindow')
  infoWindows: AgmInfoWindow[];

  @Input()
  mapMode: MapModeType = MapModeType.AllMarkers;

  @Input()
  markerModel: BuildingModel;

  @Input()
  showDashboard: boolean = true;

  @Select(BuildingState.filteredMarkers)
  filteredMarkers$: Observable<string[]>;

  @Select(BuildingState.buildingsTotal)
  buildingsTotal$: Observable<number>;

  @Select(BuildingState.markers)
  markers$: Observable<BuildingMarkerGroup[]>;

  filteredMarkers: BuildingMarkerGroup[] = [];

  @Select(PeriodState.periods)
  periods$: Observable<PeriodModel[]>;

  @Select(MunicipalityState.municipalities)
  municipalities$: Observable<MunicipalityModel[]>;

  @Select(DashboardState.statistics)
  statistics$: Observable<DashboardModel>;

  @Select(AccountState.isAuthorized)
  isAuthorized$: Observable<boolean>;

  @Select(AccountState.account)
  account$: Observable<AccountInfo>;

  @Select(BuildingState.streetRecommendations)
  streetRecommendations$: Observable<string[]>;

  @Select(BuildingState.selected)
  selected$: Observable<BuildingModel>;

  currentAccount: AccountInfo = undefined;
  filteredStreets: Observable<string[]>;

  selectedAllMunicipalities = false;
  selectedAllPeriods = false;

  defaultGuid = '00000000-0000-0000-0000-000000000000';

  // 'roadmap' | 'hybrid' | 'satellite' | 'terrain'
  mapTypes = ['roadmap', 'hybrid', 'satellite', 'terrain'];
  mapTypeId = 'roadmap';

  clusters: MapClusterModel[] = [];
  singleMarker = new BuildingMarker();
  allMarkers: BuildingMarkerGroup[] = [];

  mapModes = MapModeType;

  defaultMarker = {
    url: '/assets/icons/building-icon-gray.png',
    size: new google.maps.Size(22, 28),
    scaledSize: new google.maps.Size(22, 28),
    labelOrigin: new google.maps.Point(10, 35),
  };

  defaultZoom = 8.3;

  zoom = this.defaultZoom;
  lat = 42.008998;
  lng = 21.350201;

  selectedMunicipalityZoom = 15;
  showMarkers = true;

  dashboardModel = new DashboardModel();
  isAuthorized = false;
  municipalityForm = new FormControl();
  periodForm = new FormControl();
  streetForm = new FormControl();
  managementForm = new FormControl();

  searchLatitude = 0;
  searchLongitude = 0;

  municipalities: MunicipalityModel[];
  periods: PeriodModel[];
  streets: string[] = [];

  buildingManagementTypes = BuildingManagementType;

  buildingManagementTypeEnumValues = Object.values(
    BuildingManagementType
  ).filter((type) => isNaN(<any>type) && type !== 'values');

  subscription = new Subscription();
  streetRecommendations: string[] = [];
  buildingsTotal = 0;

  markerStyle = `{textColor: "rED" }`;

  map: any;

  yearOfBuildPreviewTranslate = '';
  municipalityTranslate = '';
  floorsTranslate = '';
  numOfApartmentsTranslate = '';
  yearOfRenovationTranslate = '';
  moreTranslate = '';
  noManagementTranslate = '';
  companyManagementTranslate = '';
  tenantsCommunityTranslate = '';

  language = '';

  constructor(
    private router: Router,
    private store: Store,
    private filterService: FilterService,
    private localizationService: LocalizationService,
    private translateService: TranslateService
  ) {
    this.translateLabels();

    this.language = this.translateService.currentLang;
  }

  closeAllOpenedInfoWindows() {
    if (this.infoWindows) {
      this.infoWindows.forEach((x) => {
        x.close();
      });
    }
  }

  ngOnInit(): void {
    this.store.dispatch(new GetAllBuildingMarkers());
    this.store.dispatch(
      new GetDashboard(
        this.translateService.currentLang
      )
    );
    this.store.dispatch(new FilterPeriods());
    this.store.dispatch(
      new FilterMunicipalitiesWithoutFiltering(
        this.translateService.currentLang
      )
    );

    this.subscribeEvents();

    if (this.mapMode === MapModeType.SingleMarker) {
      this.zoom = this.selectedMunicipalityZoom;
    } else {
      this.zoom = this.defaultZoom;
    }
  }

  translateLabels() {
    this.yearOfBuildPreviewTranslate = this.localizationService.translate(
      'year-of-build-preview'
    );
    this.municipalityTranslate =
      this.localizationService.translate('municipality');
    this.floorsTranslate = this.localizationService.translate('floors');
    this.numOfApartmentsTranslate =
      this.localizationService.translate('number-apartments');
    this.yearOfRenovationTranslate =
      this.localizationService.translate('year-renovation');
    this.moreTranslate = this.localizationService.translate('more');

    this.noManagementTranslate =
      this.localizationService.translate('no-management');
    this.companyManagementTranslate =
      this.localizationService.translate('company-manager');
    this.tenantsCommunityTranslate =
      this.localizationService.translate('tenants-community');
  }

  subscribeEvents() {
    this.subscription.add(
      this.translateService.onLangChange.subscribe((x) => {
        if (x) {
          this.language = this.translateService.currentLang;

          this.translateLabels();
          this.store.dispatch(new FilterPeriods());
          this.store.dispatch(
            new FilterMunicipalitiesWithoutFiltering(
              this.translateService.currentLang
            )
          );

          this.store.dispatch(new GetAllBuildingMarkers());
        }
      })
    );

    this.subscription.add(
      this.filteredMarkers$.subscribe((x) => {
        if (x) {
          // Filter markers
          this.filteredMarkers = [];

          this.allMarkers.forEach((group) => {
            const newGroup = new BuildingMarkerGroup();
            newGroup.markers = group.markers.filter((marker) =>
              x.includes(marker.id)
            );

            if (newGroup.markers.length > 0) {
              newGroup.municipality = group.municipality;
              this.filteredMarkers.push(newGroup);
            }
          });
        }
      })
    );

    this.subscription.add(
      this.account$.subscribe((x) => {
        if (x) {
          this.currentAccount = x;
        }
      })
    );

    this.subscription.add(
      this.streetForm.valueChanges
        .pipe(debounceTime(500))
        .subscribe((newValue) => {
          if (newValue && newValue.length > 0) {
            const searchStreet = new SearchStreet();
            searchStreet.streetName = newValue;
            searchStreet.languageType =
              LanguageType[this.translateService.currentLang.toUpperCase()];
            this.store.dispatch(new SearchByStreet(searchStreet));
          } else {
            this.streetRecommendations = [];
          }
          this.filterBuildings();
        })
    );

    this.subscription.add(
      this.periods$.subscribe((x) => {
        if (x) {
          this.periods = x;
        }
      })
    );

    this.subscription.add(
      this.isAuthorized$.subscribe((x) => {
        this.isAuthorized = x;
      })
    );

    this.subscription.add(
      this.statistics$.subscribe((x) => {
        if (x) {
          this.dashboardModel = x;
        }
      })
    );

    this.subscription.add(
      this.municipalities$.subscribe((x) => {
        if (x) {
          this.municipalities = x;
        }
      })
    );

    this.subscription.add(
      this.markers$.subscribe((x) => {
        if (x) {
          this.allMarkers = x;
        }
      })
    );

    this.subscription.add(
      this.streetRecommendations$.subscribe((x) => {
        if (x) {
          this.streetRecommendations = x;
        }
      })
    );

    this.subscription.add(
      this.buildingsTotal$.subscribe((x) => {
        if (x) {
          this.buildingsTotal = x;
        }
      })
    );

    this.subscription.add(
      this.selected$.subscribe((x) => {
        this.singleMarker = new BuildingMarker();

        if (x) {
          const marker = new BuildingMarker();
          marker.latitude = x.latitude;
          marker.longitude = x.longitude;
          marker.label = `${x.street} ${x.streetNumber}`;

          marker.construction = x.construction.toString();
          marker.id = x.id;
          marker.street = x.street;
          marker.streetNumber = x.streetNumber;
          marker.municipalityName = x.municipality.name;

          this.zoom = this.selectedMunicipalityZoom;
          this.singleMarker = marker;
        }
      })
    );
  }

  onMapReady(map: any) {
    this.map = map;
    this.map.setOptions({
      fullscreenControl: true,
      streetViewControl: true,

      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }],
        },
      ],
    });
  }

  getOptionText(option) {
    if (option) {
      return option.name;
    }
  }

  getTypeDescription(type: BuildingManagementType) {
    if (type === BuildingManagementType.NoManagement) {
      return this.noManagementTranslate;
    } else if (type === BuildingManagementType.SingleManagement) {
      return this.companyManagementTranslate;
    } else if (type === BuildingManagementType.GroupOfOwners) {
      return this.tenantsCommunityTranslate;
    }
  }

  getPeriodLabel() {
    if (this.periodForm.value && this.periodForm.value.length > 0) {
      return `${this.periodForm.value.length} (${this.dashboardModel.periods})`;
    } else {
      return this.dashboardModel.periods;
    }
  }

  getBuildingLabel() {
    if (this.buildingsTotal < this.dashboardModel.buildings) {
      return `${this.buildingsTotal} (${this.dashboardModel.buildings})`;
    }
    return this.dashboardModel.buildings;
  }

  getMunicipalityLabel() {
    if (this.municipalityForm.value && this.municipalityForm.value.length > 0) {
      return `${this.municipalityForm.value.length} (${this.dashboardModel.municipalities})`;
    } else {
      return this.dashboardModel.municipalities;
    }
  }

  resetCoordinates() {
    this.searchLongitude = 0;
    this.searchLatitude = 0;
  }

  goToDetails(id: string) {
    this.store.dispatch(new ClearSelectedBuilding());
    this.router.navigate([`/building/${id}/preview`]);
  }

  filterBuildings() {
    const municipalities = this.municipalityForm.value;
    const periods = this.periodForm.value;
    const street = this.streetForm.value;

    const types = this.managementForm.value;
    const language =
      LanguageType[this.translateService.currentLang.toUpperCase()];
    const filterModel = new BuildingFilterModel(
      municipalities,
      periods,
      street,
      '',
      types,
      this.searchLatitude,
      this.searchLongitude,
      language
    );
    if (municipalities && municipalities.length === 1) {
      this.zoom = this.selectedMunicipalityZoom;
    } else {
      this.zoom = this.defaultZoom;
    }
    this.store.dispatch(
      new FilterBuildings(this.filterService.generateHttpParams(), filterModel)
    );
  }

  transformDate(data: string) {
    return new Date(data).getFullYear();
  }

  selectAllPeriods(all: boolean) {
    this.selectedAllPeriods = all;
    if (all) {
      const array = this.periods.map((x) => x.id);
      array.push(this.defaultGuid);
      this.periodForm.setValue(array);
    } else {
      this.periodForm.setValue([]);
    }

    this.filterBuildings();
  }

  selectAllMunicipalities(all: boolean) {
    this.selectedAllMunicipalities = all;
    if (all) {
      const array = this.municipalities.map((x) => x.id);
      array.push(this.defaultGuid);
      this.municipalityForm.setValue(array);
    } else {
      this.municipalityForm.setValue([]);
    }

    this.filterBuildings();
  }

  onSelectedMunicipalities(event: any) {
    this.filterBuildings();
  }

  onSelectedManagement(event: any) {
    this.filterBuildings();
  }

  onSelectedPeriods(event: any) {
    this.filterBuildings();
  }

  getMarketTooltip(item: BuildingMarker) {
    const node = document.createElement('div');
    const construction = this.transformDate(item.construction);

    node.innerHTML = `<div class="marker-panel">
    <p class="default-color text-center bigger-font">
      <b>${item.street}
      ${item.streetNumber}</b>
    </p>
    <div class="padded">
      <p>
        <b>${this.yearOfBuildPreviewTranslate}: </b> ${construction}
      </p>

      <p>
        <b>${this.municipalityTranslate}:</b>
        ${item.municipalityName}
      </p>

      <p>
        <b>${this.floorsTranslate}: </b>
        ${item.floors}
      </p>

      <p>
        <b>${this.numOfApartmentsTranslate}: </b>
        ${item.apartments}
      </p>

      <p>
        <b>${this.yearOfRenovationTranslate}: </b>
        ${item.lastYearRenovation ? item.lastYearRenovation : ''}
      </p>

      <hr />

      ${
        this.mapMode == MapModeType.AllMarkers
          ? `<a href="/building/${item.id}/preview" class='more-details text-center no-style-anchor'><p class="more-details text-center">${this.moreTranslate}...</p></a>`
          : ``
      }
    </div>

  </div>`;

    return node;
  }

  showInfo(infoWindow: AgmInfoWindow, event: any, item: any) {
    console.log('show info');

    this.closeAllOpenedInfoWindows();

    const node = this.getMarketTooltip(item);

    if (infoWindow.content.childNodes.length > 0) {
      infoWindow.content.removeChild(infoWindow.content.firstChild);
    }

    infoWindow.content.appendChild(node);

    if (infoWindow) {
      infoWindow.open();
    }

    console.log('Show info');
  }

  closeInfo(infoWindow: any, event: any) {
    infoWindow.close();
  }

  selectedStreet() {
    this.filterBuildings();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}

export class BuildingMarkerGroup {
  municipality: string;
  markers: BuildingMarker[];
}

export class BuildingMarker {
  label: string;
  id: string;
  construction: string;
  longitude: number;
  latitude: number;
  street: string;
  streetNumber: string;

  municipalityName: string;
  floors: string;
  apartments: string;
  lastYearRenovation: string;
}
